// AccountWorkflowFilter is used to filter items in new accounting workflow ledger table
// It uses [data-for-filtering] attribute to find rows that can be filtered
// and data-starred, data-reconciled, data-todos, data-checks attributes to check for true/false values while filtering
let AccountWorkflowFilter
import $ from 'jquery'

import { getOverviewFilter } from '@app/adapters/silverfin'
import { userApi } from '@app/shared/api'
import {
  TEMPLATE_ROW_SELECTOR,
  DATA_FOR_FILTERING_ATTRIBUTE,
} from 'src/application/workflows/constants'
import debounce from 'lodash/debounce'

const FILTER_TYPES = ['todos', 'checks', 'unreconciled', 'starred']

export default AccountWorkflowFilter = (function () {
  AccountWorkflowFilter = class AccountWorkflowFilter {
    static initClass() {
      this.prototype.MAP_STATUS_TO_COLOR = {
        todos: 'success',
        checks: 'primary',
        unreconciled: 'danger',
        starred: 'warning',
      }
    }

    constructor(accountList, filterContainer) {
      this.persistedFilterStatus = getOverviewFilter()
      this.accountList = accountList
      this.filterContainer = filterContainer
      this.textFilter = this.filterContainer.find(
        'input[data-behaviour~="workflow-account-text-filter"]'
      )
      this.toggleFilter = this.filterContainer.find(
        '[data-behaviour~="workflow-account-toggle-filter"]'
      )
      this.resetFilterBtn = this.filterContainer.find(
        '[data-behaviour~="workflow-clear-filter"]'
      )

      this.toggleLoading(true)
      this.attachEvents()
      this.loadStatus()
      this.debouncedOnKeyUp = debounce((e) => {
        if (e.keyCode === 27) {
          this.resetTextFilter()
        }
        this.filterAll()
      }, 200)
    }

    attachEvents() {
      this.textFilter.on('keyup', (e) => {
        this.debouncedOnKeyUp(e)
      })

      this.toggleFilter.on('click', (e) => {
        const $target = $(e.target).closest('[data-filter-status]')
        const filterStatus = $target.data('filter-status')
        if (filterStatus !== this.persistedFilterStatus) {
          this.setToggleFilter(filterStatus)
          this.saveStatus(filterStatus)
          window.eventHub.$emit('workflows-filter-selected', e)
        } else {
          this.resetToggleFilter()
          this.saveStatus('')
          window.eventHub.$emit('workflows-filter-unselected')
        }

        this.showAllRows()
        this.filterAll()
      })

      this.toggleFilter.on('FINISHED_FETCHING', () => {
        this.toggleLoading(false)
        this.filterAll()
        this.calculateFilterCounters()
      })

      this.toggleFilter.on('REFILTER', () => {
        this.filterAll()
      })

      this.resetFilterBtn.on('click', () => {
        window.eventHub.$emit('workflows-filter-reset')
        this.resetToggleFilter()
        this.saveStatus('')
        this.filterAll()
      })

      // Delegated on the persistent `document`, so it is bound to a stable
      // reference and detached in destroy(); otherwise re-running setup()
      // (opening another workflow) stacks duplicate handlers that never go away.
      this.onStarLinkClick = (e) => {
        const $target = $(e.target)
          .closest(TEMPLATE_ROW_SELECTOR)
          .find(DATA_FOR_FILTERING_ATTRIBUTE)

        if ($target.attr('data-starred') === 'true') {
          $target.attr('data-starred', 'false')
        } else {
          $target.attr('data-starred', 'true')
        }

        this.calculateFilterCounters()
        this.filterAll()
      }

      $(document).on(
        'click',
        'a[data-behaviour~="star-link"]',
        this.onStarLinkClick
      )
    }

    destroy() {
      $(document).off(
        'click',
        'a[data-behaviour~="star-link"]',
        this.onStarLinkClick
      )
    }

    triggerEvent(name) {
      this.toggleFilter.trigger(name)
    }

    showAllRows() {
      this.triggerEvent('SHOW_ALL_ROWS')
    }

    toggleLoading(loading) {
      this.isLoading = loading
      const spinner = this.toggleFilter.find('[data-behaviour=status-spinner]')
      const icons = this.toggleFilter.find('.fa')

      if (loading) {
        spinner.removeClass('tw-hidden')
        icons.addClass('tw-hidden')
        this.toggleFilter.addClass('sf-disabled')
      } else {
        spinner.addClass('tw-hidden')
        icons.removeClass('tw-hidden')
        this.toggleFilter.removeClass('sf-disabled')
      }
    }

    // Calculates statuses for both Account AND ReconTexts
    calculateFilterCounters() {
      const countsPerType = {}

      this.accountList.find(TEMPLATE_ROW_SELECTOR).each((_idx, el) => {
        const $element = $(el).find(DATA_FOR_FILTERING_ATTRIBUTE)
        FILTER_TYPES.forEach((type) => {
          const matchesFilter = this.filterItemsComparison($element, type)

          if (matchesFilter) {
            countsPerType[type] ||= 0
            countsPerType[type] += 1
          }
        })
      })

      for (const type of FILTER_TYPES) {
        const count = countsPerType[type] || 0
        this.toggleFilter
          .find(`[data-filter-status=${type}] .count`)
          .html(count + ' ')
      }
    }

    loadStatus() {
      this.setToggleFilter(this.persistedFilterStatus)
      this.filterAll()
    }

    async saveStatus(status) {
      this.persistedFilterStatus = status

      await userApi.persistOverviewFilter(status)
    }

    resetToggleFilter() {
      this.toggleFilter.find('.sf-btn-active').removeClass('sf-btn-active')
      this.resetFiltersUI()
    }

    setToggleFilter(status) {
      this.toggleFilter.find('.sf-btn-active').removeClass('sf-btn-active')
      this.toggleFilter
        .find(`.sf-btn[data-filter-status="${status}"]`)
        .addClass('sf-btn-active')
      this.resetFiltersUI()

      const color = this.MAP_STATUS_TO_COLOR[status]

      const selectedButton = this.toggleFilter.find(
        `.sf-btn[data-filter-status="${status}"]`
      )
      selectedButton.addClass(
        `sf-bg-${color} tw-text-white hover:tw-text-white`
      )
      selectedButton
        .find('.fa')
        .removeClass(`tw-text-sf-${color}`)
        .addClass('tw-text-white hover:tw-text-white')
    }

    resetTextFilter() {
      this.setTextFilter('')
    }

    setTextFilter(text) {
      this.textFilter.val(text)
    }

    filterAll() {
      this.showEmptyStateContainer(false)

      const filterStatus = this.toggleFilter
        .find('.sf-btn-active')
        .data('filter-status')

      const query = this.textFilter.val().toLowerCase()
      const isNumericQuery = $.isNumeric(query[0])
      const haveQuery = query !== ''

      let hasItems = false
      const groupIdsToShow = new Set()

      this.accountList.find(TEMPLATE_ROW_SELECTOR).each((_idx, rawEl) => {
        const el = $(rawEl)
        const filteringElement = el.find(DATA_FOR_FILTERING_ATTRIBUTE)
        const matchesFilter = this.filterItemsComparison(
          filteringElement,
          filterStatus
        )

        if (!matchesFilter) {
          el.hide()
          return true
        }

        if (haveQuery) {
          const foundAt = el
            .find('[data-behaviour="account-name"]')
            .text()
            .trim()
            .toLowerCase()
            .indexOf(query)

          const matchesQuery = isNumericQuery ? foundAt === 0 : foundAt > -1

          if (!matchesQuery) {
            el.hide()
            return true
          }
        }

        if (el.attr('data-row-hidden') !== 'true') {
          el.show()
          hasItems = true
        }

        const parentGroupIdsData = el.attr('data-parent-group-ids')

        if (parentGroupIdsData) {
          const parentGroupIds = parentGroupIdsData.split(',')
          parentGroupIds.forEach((id) => groupIdsToShow.add(id))
        }
      })

      this.accountList
        .find(`[data-group-id]:not([data-row-hidden=true])`)
        .each((_idx, rawEl) => {
          const el = $(rawEl)
          const groupId = el.attr('data-group-id')
          const visible = groupIdsToShow.has(groupId)
          el.toggle(visible)

          hasItems ||= visible
        })

      this.toggleReconciliationsVisibilityBasedOnContent()
      this.toggleAccountListingHeaderVisibility()

      this.showEmptyStateContainer(!hasItems && !this.isLoading)
    }

    toggleReconciliationsVisibilityBasedOnContent() {
      const $tbody = $('[data-behaviour~="reconciliation-texts"]')
      $tbody.show() // needs to be visible for the row visibility filter to work

      if ($tbody.find('tr:visible').length === 0) {
        $tbody.hide()
      }
    }

    toggleAccountListingHeaderVisibility() {
      const $table = $('[data-behaviour~="table-reconciliation"]')

      const $container = $table.parents(
        '[data-behaviour~="reconciliation-template-container"]'
      )
      $container.show()
    }

    showEmptyStateContainer(show) {
      const $container = this.accountList.parents(
        '[data-behaviour~="reconciliation-template-container"]'
      )
      const $emptyContainer = $container.find(
        '[data-behaviour~="empty-state-container"]'
      )

      if (show) {
        this.accountList.addClass('sf-hidden')
        $emptyContainer.removeClass('sf-hidden')
      } else {
        this.accountList.removeClass('sf-hidden')
        $emptyContainer.addClass('sf-hidden')
      }
    }

    filterItemsComparison($item, type) {
      switch (type) {
        case 'checks':
          return $item.attr('data-checks') === 'true'
        case 'todos':
          return $item.attr('data-todos') === 'true'
        case 'unreconciled':
          return (
            $item.attr('data-reconciled') === 'false' &&
            $item.attr('data-starred') === 'true'
          )

        case 'starred':
          return $item.attr('data-starred') === 'true'
        default:
          return true
      }
    }

    resetFiltersUI() {
      FILTER_TYPES.forEach((status) => {
        const color = this.MAP_STATUS_TO_COLOR[status]
        this.toggleFilter
          .find(`.sf-btn[data-filter-status="${status}"]`)
          .removeClass(`sf-bg-${color} tw-text-white hover:tw-text-white`)

        this.toggleFilter
          .find(`.sf-btn[data-filter-status="${status}"] .fa`)
          .addClass(`tw-text-sf-${color}`)
          .removeClass('tw-text-white hover:tw-text-white')
      })
    }
  }
  AccountWorkflowFilter.initClass()
  return AccountWorkflowFilter
})()
